/**
 * Detalle BHE: datos parseados + acciones (descartar, descargar XML/PDF).
 * Edición inline de campos relevantes. Posteo llegará en PR 3.
 */
import { formatNumberForTenant, useTenantLocale } from '@/lib/locale'
import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, Download, ExternalLink, Trash2, Wallet } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { Badge } from '@/components/Badge'
import { Button } from '@/components/Button'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import DatePicker from '@/components/DatePicker'
import { useToast } from '@/components/Toast'
import { displayLabelCls, inputCls, labelCls } from '@/lib/formStyles'
import apiClient from '@/lib/apiClient'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { NumericField } from '@/components/ds/NumericField'
import { PAYMENT_STATUS_LABEL, type HonorariosPayment } from '../types'
import {
  useCancelHonorariosPayment,
  useDiscardHonorarios,
  useHonorariosDetail,
  usePayHonorarios,
  usePostHonorarios,
  useUpdateHonorarios,
} from '../hooks/useHonorarios'
import { Modal } from '@/components/ds/Modal'

const PAY_METHODS: { value: string; label: string }[] = [
  { value: 'transfer', label: 'Transferencia' },
  { value: 'check', label: 'Cheque' },
  { value: 'cash', label: 'Efectivo' },
  { value: 'servipag', label: 'Servipag' },
  { value: 'other', label: 'Otro' },
]


function fmt(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return String(value)
  return formatNumberForTenant(num, 0)
}


export function HonorariosDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: bill, isLoading } = useHonorariosDetail(id)
  const update = useUpdateHonorarios()
  const discard = useDiscardHonorarios()
  const post = usePostHonorarios()
  const pay = usePayHonorarios()
  const cancelPay = useCancelHonorariosPayment()
  const { data: accountsData } = useAccounts({ is_active: 'true', is_imputable: 'true' })
  const locale = useTenantLocale()

  const accountItems = useMemo<AutocompleteItem[]>(() => {
    const list = (accountsData as any)?.accounts ?? []
    return list.map((a: any) => ({ id: a.id, label: `${a.code} — ${a.name}` }))
  }, [accountsData])

  const downloadFile = async (kind: 'pdf' | 'xml') => {
    try {
      const res = await apiClient.get(`/honorarios/${id}/${kind}/`, { responseType: 'blob' })
      const blobUrl = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = (kind === 'pdf' ? bill?.pdf_file_name : bill?.xml_file_name) || `bhe.${kind}`
      document.body.appendChild(a); a.click(); a.remove()
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    } catch (exc: any) {
      toast.error(exc?.response?.data?.error?.message || `Error al descargar ${kind.toUpperCase()}.`)
    }
  }
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()

  const [issuerName, setIssuerName] = useState('')
  const [bheNumber, setBheNumber] = useState('')
  const [issueDate, setIssueDate] = useState('')
  const [description, setDescription] = useState('')
  const [expenseAccount, setExpenseAccount] = useState<string>('')

  // Formulario de registro de pago del líquido
  const [showPayForm, setShowPayForm] = useState(false)
  const [payDate, setPayDate] = useState('')
  const [payMethod, setPayMethod] = useState('transfer')
  const [payBank, setPayBank] = useState('')
  const [payRef, setPayRef] = useState('')
  const [payAmount, setPayAmount] = useState('')

  useEffect(() => {
    if (!bill) return
    setIssuerName(bill.issuer_name)
    setBheNumber(bill.bhe_number)
    setIssueDate(bill.issue_date ?? '')
    setDescription(bill.description)
    setExpenseAccount(bill.expense_account ?? '')
  }, [bill])


  if (isLoading || !bill) {
    return (
      <Modal open onClose={onClose} size="lg" title="Boleta de honorarios">
        <p className="text-sm text-content-muted">Cargando…</p>
      </Modal>
    )
  }

  const readOnly = bill.status === 'posted' || bill.status === 'discarded'

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        id, data: {
          issuer_name: issuerName,
          bhe_number: bheNumber,
          issue_date: issueDate || null,
          description,
          expense_account: expenseAccount || null,
        } as any,
      })
      toast.success('Cambios guardados.')
    } catch (exc: any) {
      toast.error(exc?.response?.data?.error?.message || 'Error al guardar.')
    }
  }

  const handlePost = () => {
    if (!expenseAccount) {
      toast.error('Seleccionar una cuenta de gasto antes de contabilizar.')
      return
    }
    confirm({
      message: 'Se creará el comprobante contable de la boleta (gasto / honorarios por pagar / retención). El pago del líquido se registra después, dentro de este módulo. Esta acción no se puede deshacer automáticamente.',
      variant: 'warning',
      confirmLabel: 'Contabilizar',
      onConfirm: async () => {
        try {
          // Guardar cambios pendientes primero (incluye expense_account)
          if (bill && expenseAccount !== (bill.expense_account ?? '')) {
            await update.mutateAsync({ id, data: { expense_account: expenseAccount } as any })
          }
          await post.mutateAsync(id)
          toast.success('BHE contabilizada. Ver comprobante en el detalle.')
          onClose()
        } catch (exc: any) {
          toast.error(exc?.response?.data?.error?.message || 'Error al contabilizar.')
        }
      },
    })
  }

  const handleDiscard = () => {
    confirm({
      message: 'La BHE quedará marcada como descartada y no aparecerá en los cálculos.',
      variant: 'danger', confirmLabel: 'Descartar',
      onConfirm: async () => {
        try {
          await discard.mutateAsync(id)
          toast.success('BHE descartada.')
          onClose()
        } catch { toast.error('Error al descartar.') }
      },
    })
  }

  const saldo = parseFloat(bill.pending_amount) || 0
  const montoPago = parseFloat(payAmount) || 0
  const montoInvalido = montoPago <= 0 || montoPago > saldo

  const openPayForm = () => {
    // Por defecto se paga el saldo completo; se rebaja para registrar un abono.
    setPayAmount(String(saldo))
    setShowPayForm(true)
  }

  const handlePay = async () => {
    if (!payBank) { toast.error('Falta la cuenta de banco/caja.'); return }
    if (!payDate) { toast.error('Falta la fecha de pago.'); return }
    if (montoInvalido) { toast.error(`El monto debe ser mayor a cero y no superar el saldo (${fmt(saldo)}).`); return }
    try {
      const r = await pay.mutateAsync({
        id,
        data: {
          payment_date: payDate, bank_account_id: payBank, payment_method: payMethod,
          reference: payRef, amount: payAmount,
        },
      })
      toast.success(r.bill.payment_status === 'paid'
        ? 'Pago registrado. La boleta quedó pagada.'
        : `Abono registrado. Saldo por pagar: ${fmt(r.pending_amount)}.`)
      setShowPayForm(false)
      setPayRef('')
    } catch (exc: any) {
      toast.error(exc?.response?.data?.error?.message || 'Error al registrar el pago.')
    }
  }

  const handleCancelPay = (p: HonorariosPayment) => {
    confirm({
      message: `Se anulará el pago de ${fmt(p.amount)} del ${p.payment_date} y se eliminará su comprobante (si está en borrador). El monto vuelve al saldo por pagar.`,
      variant: 'danger', confirmLabel: 'Anular pago',
      onConfirm: async () => {
        try {
          await cancelPay.mutateAsync({ id, paymentId: p.id })
          toast.success('Pago anulado.')
        } catch (exc: any) {
          toast.error(exc?.response?.data?.error?.message || 'Error al anular el pago.')
        }
      },
    })
  }

  const estadoPago = PAYMENT_STATUS_LABEL[bill.payment_status] ?? PAYMENT_STATUS_LABEL.pending

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={`BHE ${bill.bhe_number || '—'}`}
        subtitle={
        <span className="flex items-center gap-2">
          <Badge
            label={bill.status}
            variant={
              bill.status === 'posted' ? 'emerald' :
              bill.status === 'discarded' ? 'slate' :
              bill.status === 'error' ? 'red' : 'blue'
            }
          />
          {bill.retiene_emisor && <Badge label="Emisor retuvo" variant="amber" />}
        </span>
      }
        dismissOnScrimClick={false}
        actions={
          <>
            <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
            <div className="flex gap-3">
              {bill.pdf_file_name && (
                <button onClick={() => downloadFile('pdf')}
                   className="text-xs text-primary-700 hover:text-primary-900 flex items-center gap-1">
                  <Download size={13} /> PDF
                </button>
              )}
              {bill.xml_file_name && (
                <button onClick={() => downloadFile('xml')}
                   className="text-xs text-primary-700 hover:text-primary-900 flex items-center gap-1">
                  <Download size={13} /> XML
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {!readOnly && (
                <>
                  <Button variant="ghost" leftIcon={<Trash2 size={14} />} onClick={handleDiscard}>Descartar</Button>
                  <Button variant="secondary" onClick={handleSave} isPending={update.isPending} pendingLabel="Guardando…">
                    Guardar
                  </Button>
                  <Button
                    leftIcon={<CheckCircle2 size={14} />}
                    onClick={handlePost}
                    isPending={post.isPending}
                    pendingLabel="Contabilizando…"
                    disabled={!expenseAccount}
                  >
                    Contabilizar
                  </Button>
                </>
              )}
            </div>
            </div>
          </>
        }
      >
        <div className="space-y-5">
          {bill.parse_warnings.length > 0 && (
            <div className="bg-warning-surface border border-warning-border rounded-lg p-3 text-xs text-warning-content-strong">
              <div className="flex items-center gap-1.5 font-semibold mb-1">
                <AlertTriangle size={13} /> Avisos del parser
              </div>
              <ul className="list-disc list-inside space-y-0.5">
                {bill.parse_warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <section className="grid grid-cols-2 gap-3">
            <div>
              <label className={displayLabelCls}>RUT emisor</label>
              <div className="text-sm text-content font-mono">{bill.issuer_rut || '—'}</div>
            </div>
            <div>
              <label htmlFor="bhe-nombre-emisor" className={labelCls}>Nombre emisor</label>
              {readOnly ? (
                <div className="text-sm text-content">{bill.issuer_name || '—'}</div>
              ) : (
                <input id="bhe-nombre-emisor" type="text" value={issuerName} onChange={e => setIssuerName(e.target.value)} className={inputCls} />
              )}
            </div>
            <div>
              <label className={displayLabelCls}>Actividad</label>
              <div className="text-sm text-content">{bill.issuer_activity || '—'}</div>
            </div>
            <div>
              <label htmlFor="bhe-numero" className={labelCls}>N° BHE</label>
              {readOnly ? (
                <div className="text-sm font-mono text-content">{bill.bhe_number || '—'}</div>
              ) : (
                <input id="bhe-numero" type="text" value={bheNumber} onChange={e => setBheNumber(e.target.value)} className={inputCls} />
              )}
            </div>
            <div>
              <label htmlFor="bhe-fecha" className={labelCls}>Fecha</label>
              {readOnly ? (
                <div className="text-sm text-content">{bill.issue_date ?? '—'}</div>
              ) : (
                <DatePicker id="bhe-fecha" value={issueDate} onChange={setIssueDate} />
              )}
            </div>
            <div>
              <label className={displayLabelCls}>% Retención</label>
              <div className="text-sm text-content">{bill.porcentaje_impuesto}%</div>
            </div>
          </section>

          <section>
            <label htmlFor="bhe-descripcion" className={labelCls}>Descripción</label>
            {readOnly ? (
              <div className="text-sm text-content whitespace-pre-wrap">{bill.description || '—'}</div>
            ) : (
              <textarea id="bhe-descripcion" value={description} onChange={e => setDescription(e.target.value)}
                        rows={3} className={inputCls} />
            )}
          </section>

          <section>
            <label htmlFor="bhe-cuenta-gasto" className={labelCls}>
              Cuenta de gasto {!expenseAccount && !readOnly && <span className="text-danger-solid">*</span>}
            </label>
            {readOnly ? (
              <div className="text-sm text-content">
                {bill.expense_account_code
                  ? `${bill.expense_account_code} — ${bill.expense_account_name}`
                  : '—'}
              </div>
            ) : (
              <Autocomplete id="bhe-cuenta-gasto"
                items={accountItems}
                value={expenseAccount}
                displayValue={accountItems.find(i => i.id === expenseAccount)?.label ?? ''}
                onChange={() => { /* query interno */ }}
                onSelect={(item) => setExpenseAccount(item.id)}
                placeholder="Buscar cuenta por código o nombre…"
                className={inputCls}
              />
            )}
          </section>

          <section className="bg-glass-3 rounded-lg p-3 grid grid-cols-3 gap-3 text-xs">
            <div>
              <div className="text-content-muted">Total (bruto)</div>
              <div className="text-content font-semibold mt-0.5">{fmt(bill.total_honorarios)}</div>
            </div>
            <div>
              <div className="text-content-muted">Retención 2da cat.</div>
              <div className="text-content font-semibold mt-0.5">{fmt(bill.impuesto_honorarios)}</div>
            </div>
            <div>
              <div className="text-content-muted">Líquido a pagar</div>
              <div className="text-content font-semibold mt-0.5">{fmt(bill.liquido_honorarios)}</div>
            </div>
          </section>

          {bill.status === 'posted' && (
            <section className="border-t border-divider pt-3">
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls}>Pago del líquido</label>
                <Badge label={estadoPago.label} variant={estadoPago.variant} />
              </div>

              {bill.payment_status !== 'pending' && (
                <div className="mb-3 grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="text-content-muted">Pagado</div>
                    <div className="text-content font-semibold mt-0.5">{fmt(bill.paid_amount)}</div>
                  </div>
                  <div>
                    <div className="text-content-muted">Saldo por pagar</div>
                    <div className="text-content font-semibold mt-0.5">{fmt(bill.pending_amount)}</div>
                  </div>
                </div>
              )}

              {bill.payments.length > 0 && (
                <ul className="mb-3 divide-y divide-divider rounded-lg border border-divider text-xs">
                  {bill.payments.map(p => (
                    <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0 space-y-0.5">
                        <div className="text-content">
                          <span className="font-semibold">{fmt(p.amount)}</span>
                          <span className="text-content-muted"> · {p.payment_date} · {p.payment_method_display || '—'}</span>
                        </div>
                        <div className="text-content-subtle">
                          {p.bank_account_code ? `${p.bank_account_code} — ${p.bank_account_name}` : '—'}
                          {p.reference && ` · Ref. ${p.reference}`}
                          {p.voucher && (
                            <>
                              {' · '}
                              <Link
                                to={`/accounting/vouchers?highlight=${p.voucher}`}
                                onClick={onClose}
                                className="font-mono text-success-content underline decoration-dotted inline-flex items-center gap-0.5"
                              >
                                {p.voucher_number || 'Ver comprobante'}
                                <ExternalLink size={10} />
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleCancelPay(p)}
                        disabled={cancelPay.isPending}
                      >
                        Anular
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              {bill.payment_status === 'paid' ? null : showPayForm ? (
                <div className="space-y-3">
                  <NumericField
                    id="honorariosdetailmodal-monto"
                    label="Monto a pagar"
                    value={payAmount}
                    onChange={setPayAmount}
                    locale={locale}
                    decimals={0}
                    size="sm"
                    hint={`Saldo por pagar: ${fmt(saldo)}. Un monto menor queda como abono; la retención no cambia.`}
                    error={payAmount && montoInvalido ? 'Debe ser mayor a cero y no superar el saldo.' : undefined}
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="honorariosdetailmodal-fecha-de-pago" className={labelCls}>Fecha de pago <span className="text-danger-solid">*</span></label>
                      <DatePicker id="honorariosdetailmodal-fecha-de-pago" value={payDate} onChange={setPayDate} />
                    </div>
                    <SelectField
                      dense
                      label="Método"
                      value={payMethod}
                      onChange={e => setPayMethod(e.target.value)}
                    >
                      {PAY_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </SelectField>
                  </div>
                  <div>
                    <label htmlFor="honorariosdetailmodal-cuenta-de-banco-caja" className={labelCls}>Cuenta de banco / caja <span className="text-danger-solid">*</span></label>
                    <Autocomplete id="honorariosdetailmodal-cuenta-de-banco-caja"
                      items={accountItems}
                      value={payBank}
                      displayValue={accountItems.find(i => i.id === payBank)?.label ?? ''}
                      onChange={() => { /* query interno */ }}
                      onSelect={(item) => setPayBank(item.id)}
                      placeholder="Buscar cuenta por código o nombre…"
                      className={inputCls}
                    />
                  </div>
                  <TextField
                    dense
                    label="Referencia"
                    value={payRef}
                    onChange={e => setPayRef(e.target.value)}
                    placeholder="N° transferencia / cheque…"
                    inputProps={{ type: 'text' }}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => setShowPayForm(false)}>Cancelar</Button>
                    <Button onClick={handlePay} isPending={pay.isPending} pendingLabel="Registrando…" disabled={!payBank || !payDate || montoInvalido}>
                      {montoPago > 0 && montoPago < saldo ? 'Registrar abono' : 'Registrar pago'}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="secondary" leftIcon={<Wallet size={14} />} onClick={openPayForm}>
                  Registrar pago
                </Button>
              )}
            </section>
          )}

          <section className="border-t border-divider pt-3 text-xs text-content-subtle space-y-0.5">
            <div>Origen: {bill.source === 'email' ? `Email (${bill.received_from_email})` : 'Upload manual'}</div>
            {bill.pdf_file_name && <div>PDF: {bill.pdf_file_name}</div>}
            {bill.xml_file_name && <div>XML: {bill.xml_file_name}</div>}
            {bill.codigo_alfa && <div>Código alfa SII: <span className="font-mono">{bill.codigo_alfa}</span></div>}
            {bill.voucher && (
              <div>
                Comprobante:{' '}
                <Link
                  to={`/accounting/vouchers?highlight=${bill.voucher}`}
                  onClick={onClose}
                  className="font-mono text-success-content underline decoration-dotted hover:text-emerald-900 inline-flex items-center gap-0.5"
                >
                  {bill.voucher_number || 'Ver comprobante'}
                  <ExternalLink size={10} />
                </Link>
              </div>
            )}
          </section>
        </div>
      </Modal>
      <ConfirmModal {...modalProps} />
    </>
  )
}
